/**
 * Schemas Zod compartilhados (Seção 4): validam tanto o formulário no renderer
 * quanto o payload IPC no processo principal — uma única fonte de verdade.
 */
import { z } from 'zod'

export const loginSchema = z.object({
  login: z.string().trim().min(1, 'Informe o usuário'),
  senha: z.string().min(1, 'Informe a senha'),
})
export type LoginInput = z.infer<typeof loginSchema>

// Texto opcional: normaliza '' (e nulo/indefinido) para null.
const optText = z.preprocess(
  (v) => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim()) : (v ?? null)),
  z.string().nullable(),
)
const optEmail = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null),
  z.string().email('E-mail inválido').nullable(),
)
// Número opcional: '' / null → null; aceita vírgula decimal vinda do formulário.
const optNum = z.preprocess(
  (v) => {
    if (v === '' || v === null || v === undefined) return null
    if (typeof v === 'string') return Number(v.replace(',', '.'))
    return v
  },
  z.number({ invalid_type_error: 'Valor numérico inválido' }).nullable(),
)

// ===== Clientes (Seção 9.1) =====
export const clienteSchema = z
  .object({
    tipoPessoa: z.enum(['PJ', 'PF']),
    razaoSocial: z.string().trim().min(1, 'Informe a razão social / nome'),
    nomeFantasia: optText,
    cnpjCpf: z.string().trim().min(1, 'Informe o CNPJ/CPF'),
    inscricaoEstadual: optText,
    segmentoIndustria: optText,
    endereco: optText,
    cidade: optText,
    uf: optText,
    cep: optText,
    telefone: optText,
    email: optEmail,
    contatoPrincipal: optText,
    observacoes: optText,
  })
  .superRefine((val, ctx) => {
    const digitos = val.cnpjCpf.replace(/\D/g, '')
    const esperado = val.tipoPessoa === 'PJ' ? 14 : 11
    if (digitos.length !== esperado) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cnpjCpf'],
        message: val.tipoPessoa === 'PJ' ? 'CNPJ deve ter 14 dígitos' : 'CPF deve ter 11 dígitos',
      })
    }
  })
export type ClienteInput = z.infer<typeof clienteSchema>

// ===== Vendas (Seção 9.5) =====
export const vendaItemSchema = z.object({
  modeloId: z.number().int().positive('Selecione um modelo'),
  quantidade: z.number().int('Use um inteiro').positive('Quantidade deve ser maior que zero'),
  precoUnitario: z.number().nonnegative('Preço não pode ser negativo'),
  descontoItem: z.number().nonnegative().default(0),
  // Proposta: impostos em PERCENTUAL (não em moeda), prazo e especificação por item.
  icmsPercent: z.number().min(0).max(100).nullable().optional(),
  ipiPercent: z.number().min(0).max(100).nullable().optional(),
  prazoEntrega: optText,
  especificacao: optText,
})

export const vendaSchema = z.object({
  clienteId: z.number().int().positive('Selecione o cliente'),
  dataVenda: optText,
  formaPagamento: z.preprocess(
    (v) => (v === '' || v == null ? null : v),
    z.enum(['PIX', 'BOLETO', 'TRANSFERENCIA', 'CARTAO', 'OUTRO']).nullable(),
  ),
  condicaoPagamento: optText,
  desconto: z.number().nonnegative().default(0),
  observacoes: optText,
  // Proposta comercial (PDF padrão Werc).
  numeroProposta: optText,
  localidade: optText,
  nomeProjeto: optText,
  contatoNome: optText,
  contatoCargo: optText,
  contatoTelefone: optText,
  contatoEmail: optText,
  itens: z.array(vendaItemSchema).min(1, 'Adicione ao menos um item'),
})
export type VendaInput = z.infer<typeof vendaSchema>

export const statusVendaManual = z.enum(['FATURADO', 'ENTREGUE', 'CANCELADO'])

// ===== Integração VHSys (v0.1.1) =====
export const vhsysConfigSchema = z.object({
  accessToken: z.string().trim().min(1, 'Informe o Access-Token'),
  secretAccessToken: z.string().trim().min(1, 'Informe o Secret-Access-Token'),
})
export type VhsysConfigInput = z.infer<typeof vhsysConfigSchema>

// ===== Importações (Seção 9.4) =====
export const importacaoItemSchema = z.object({
  modeloId: z.number().int().positive('Selecione um modelo'),
  quantidade: z.number().int('Use um inteiro').positive('Quantidade deve ser maior que zero'),
  custoUnitarioOrigem: z.number().nonnegative('Custo não pode ser negativo'),
  numeroSerie: optText,
  lote: optText,
})

export const importacaoSchema = z.object({
  numeroInvoice: z.string().trim().min(1, 'Informe o número da invoice'),
  fornecedorId: z.number().int().positive('Selecione o fornecedor'),
  dataPedido: optText,
  dataEmbarque: optText,
  dataChegadaPrevista: optText,
  moeda: z.string().trim().min(1).default('EUR'),
  taxaCambio: z.number().positive('Câmbio deve ser positivo').nullable().optional(),
  valorFrete: z.number().nonnegative().default(0),
  valorSeguro: z.number().nonnegative().default(0),
  valorImpostos: z.number().nonnegative().default(0),
  valorOutrasDespesas: z.number().nonnegative().default(0),
  observacoes: optText,
  itens: z.array(importacaoItemSchema).min(1, 'Adicione ao menos um item'),
})
export type ImportacaoInput = z.infer<typeof importacaoSchema>

export const alocacaoSchema = z.object({
  importacaoItemId: z.number().int().positive(),
  clienteId: z.number().int().positive('Selecione o cliente'),
  quantidade: z.number().int('Use um inteiro').positive('Quantidade deve ser maior que zero'),
  // Pedido do cliente (alfanumérico livre, manual). Opcional.
  pedidoCliente: optText,
  observacao: optText,
})
export type AlocacaoInput = z.infer<typeof alocacaoSchema>

export const statusImportacaoManual = z.enum(['PEDIDO', 'EM_TRANSITO', 'DESEMBARACO', 'CANCELADO'])

// ===== Ajuste manual de estoque (inventário físico — Seção 9.3) =====
export const ajusteEstoqueSchema = z.object({
  modeloId: z.number().int().positive(),
  tipo: z.enum(['ENTRADA', 'SAIDA']),
  quantidade: z.number().int('Use um inteiro').positive('Quantidade deve ser maior que zero'),
  observacao: z.string().trim().min(1, 'A observação é obrigatória no ajuste de inventário'),
  // ENTRADA de modelo serializado: séries opcionais, por unidade (vazio = pendente).
  series: z.array(z.string()).optional(),
  // SAÍDA de modelo serializado: unidades específicas escolhidas para baixa.
  unidadeIds: z.array(z.number().int().positive()).optional(),
})
export type AjusteEstoqueInput = z.infer<typeof ajusteEstoqueSchema>

// ===== Atualização de modelo do catálogo (identificação + comercial/estoque) =====
export const modeloUpdateSchema = z.object({
  // Identificação (edição completa do modelo)
  codigoModelo: z.string().trim().min(1, 'Informe o código do modelo').optional(),
  descricaoCurta: optText,
  unidadeMedida: z.string().trim().min(1).optional(),
  pesoKg: optNum.refine((v) => v === null || v >= 0, 'Peso não pode ser negativo'),
  moedaCusto: z.string().trim().min(1).optional(),
  // Comercial / estoque
  custoPadrao: optNum.refine((v) => v === null || v >= 0, 'Custo não pode ser negativo'),
  precoVendaPadrao: optNum.refine((v) => v === null || v >= 0, 'Preço não pode ser negativo'),
  estoqueMinimo: z.number().int('Use um inteiro').nonnegative('Não pode ser negativo').optional(),
  ncm: z.string().trim().nullable().optional(),
  ativo: z.boolean().optional(),
  serializado: z.boolean().optional(),
})
export type ModeloUpdateInput = z.infer<typeof modeloUpdateSchema>

// ===== Edição completa de um produto existente (nome, linha, norma, tag…) =====
export const produtoUpdateSchema = z
  .object({
    nomeComercial: z.string().trim().min(1, 'Informe o nome do produto'),
    linhaId: z.number().int().positive().nullable().optional(),
    novaLinha: z.string().trim().min(1).nullable().optional(),
    fabricante: z.string().trim().min(1).default('RSBP spol. s r.o.'),
    paisOrigem: z.string().trim().min(1).default('República Tcheca'),
    normaCertificacao: optText,
    descricao: optText,
    tag: optText,
  })
  .refine((d) => d.linhaId != null || (d.novaLinha && d.novaLinha.trim() !== ''), {
    message: 'Escolha uma linha existente ou informe uma nova',
    path: ['linhaId'],
  })
export type ProdutoUpdateInput = z.infer<typeof produtoUpdateSchema>

// ===== Cadastro de catálogo (Seção 9.2 — criar produtos/modelos/linhas) =====

// Especificação técnica (atributo → valor). Linhas em branco são descartadas a montante.
export const especificacaoSchema = z.object({
  atributo: z.string().trim().min(1, 'Informe o atributo'),
  valor: z.string().trim().min(1, 'Informe o valor'),
})
export type EspecificacaoInput = z.infer<typeof especificacaoSchema>

// Lista completa de especificações de um modelo (substitui as existentes).
export const especificacoesUpdateSchema = z.array(especificacaoSchema)
export type EspecificacoesUpdateInput = z.infer<typeof especificacoesUpdateSchema>

// Nova linha/categoria de produto.
export const linhaCreateSchema = z.object({
  nome: z.string().trim().min(1, 'Informe o nome da linha'),
  descricao: optText,
})
export type LinhaCreateInput = z.infer<typeof linhaCreateSchema>

// Modelo (SKU). Reusado ao criar um produto inteiro ou um modelo avulso.
export const modeloCreateSchema = z.object({
  codigoModelo: z.string().trim().min(1, 'Informe o código do modelo'),
  descricaoCurta: optText,
  unidadeMedida: z.string().trim().min(1).default('UN'),
  pesoKg: optNum.refine((v) => v === null || v >= 0, 'Peso não pode ser negativo'),
  ncm: optText,
  moedaCusto: z.string().trim().min(1).default('EUR'),
  custoPadrao: optNum.refine((v) => v === null || v >= 0, 'Custo não pode ser negativo'),
  precoVendaPadrao: optNum.refine((v) => v === null || v >= 0, 'Preço não pode ser negativo'),
  estoqueMinimo: z.number().int('Use um inteiro').nonnegative('Não pode ser negativo').default(0),
  serializado: z.boolean().default(false),
  especificacoes: especificacoesUpdateSchema.default([]),
})
export type ModeloCreateInput = z.infer<typeof modeloCreateSchema>

// Produto (família comercial) + seus modelos. Linha: existente (linhaId) OU nova (novaLinha).
export const produtoCreateSchema = z
  .object({
    linhaId: z.number().int().positive().nullable().optional(),
    novaLinha: z.string().trim().min(1).nullable().optional(),
    fornecedorId: z.number().int().positive().optional(),
    nomeComercial: z.string().trim().min(1, 'Informe o nome do produto'),
    fabricante: z.string().trim().min(1).default('RSBP spol. s r.o.'),
    paisOrigem: z.string().trim().min(1).default('República Tcheca'),
    normaCertificacao: optText,
    descricao: optText,
    tag: optText,
    modelos: z.array(modeloCreateSchema).min(1, 'Adicione ao menos um modelo'),
  })
  .refine((d) => d.linhaId != null || (d.novaLinha && d.novaLinha.trim() !== ''), {
    message: 'Escolha uma linha existente ou informe uma nova',
    path: ['linhaId'],
  })
export type ProdutoCreateInput = z.infer<typeof produtoCreateSchema>

// Modelo avulso adicionado a um produto já existente.
export const modeloAvulsoSchema = modeloCreateSchema.extend({
  produtoId: z.number().int().positive(),
})
export type ModeloAvulsoInput = z.infer<typeof modeloAvulsoSchema>

// ===== Pedidos (Werc 2.0 — F2) =====
const textoOpc = z.string().max(2000).nullable().optional().transform((v) => (v === undefined || v === null || v.trim() === '' ? null : v))
export const itemPedidoSchema = z.object({
  quantidade: z.number().nonnegative('Quantidade não pode ser negativa').nullable(),
  produto: textoOpc,
  complemento: textoOpc,
})
/** Colunas A..N como o usuário digita (texto livre, como na planilha) + itens estruturados. */
export const pedidoInputSchema = z.object({
  ano: z.number().int().min(2000).max(2100),
  stat: textoOpc,
  cliente: z.string().trim().min(1, 'Informe o cliente').max(300),
  pedido: textoOpc,
  orderWerc: textoOpc,
  proposta: textoOpc,
  dataEntrega: textoOpc,
  entregou: textoOpc,
  transportadora: textoOpc,
  valor: z.number().nullable(),
  aReceber: z.number().nullable(),
  nf: textoOpc,
  prevPgmto: textoOpc,
  ok: textoOpc,
  itens: z.array(itemPedidoSchema).max(200),
  statusId: z.number().int().min(1).max(99).nullable(),
})
export type PedidoInput = z.input<typeof pedidoInputSchema>

// ===== Chat (F4) =====
export const chatAnexoSchema = z.object({
  nome: z.string().trim().min(1).max(255),
  mime: z.string().trim().min(1).max(120),
  tamanho: z.number().int().nonnegative().max(25 * 1024 * 1024, 'Anexo maior que 25 MB.'),
  chave: z.string().regex(/^[0-9a-f]{64}$/, 'Anexo inválido.'),
})
export const chatEnviarSchema = z.object({
  canalId: z.number().int().positive(),
  texto: z.string().max(8000, 'Mensagem muito longa (máx. 8000 caracteres).').default(''),
  threadId: z.number().int().positive().nullable().optional(),
  anexos: z.array(chatAnexoSchema).max(10, 'No máximo 10 anexos por mensagem.').default([]),
})
export type ChatEnviarInput = z.input<typeof chatEnviarSchema>

// ===== Docs (F5) =====
/** Identificadores gerados no computador (crypto.randomUUID) e fichas de revisão. */
export const docIdSchema = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, 'Identificador inválido.')
export const docTituloSchema = z.string().trim().min(1, 'Informe um nome.').max(200, 'Nome muito longo (máx. 200 caracteres).')
export const docCriarSchema = z.object({
  id: docIdSchema,
  titulo: docTituloSchema,
  pastaId: docIdSchema.nullable().optional(),
  revisao: docIdSchema,
  conteudo: z.string().optional(),
})
export type DocCriarInput = z.input<typeof docCriarSchema>
export const docPastaSchema = z.object({
  id: docIdSchema,
  nome: docTituloSchema,
  paiId: docIdSchema.nullable().optional(),
})
export type DocPastaInput = z.input<typeof docPastaSchema>
